import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Eye, EyeOff } from 'lucide-react';

import { Button } from 'src/components/ui/button';
import { cn } from 'src/lib/utils';

const inputVariants = cva(
  'flex w-full rounded-md border border-input bg-white px-3 py-1 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-input-placeholder placeholder:font-light focus-visible:outline-hidden focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-secondary disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive',
  {
    variants: {
      size: {
        default: 'h-9',
        large: 'h-11',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

type InputProps = Omit<React.ComponentProps<'input'>, 'size'> & VariantProps<typeof inputVariants>;

const Input = ({ className, type, size, ...props }: InputProps) => {
  return <input type={type} className={cn(inputVariants({ size }), className)} {...props} />;
};

const InputPassword = ({ className, ...props }: InputProps) => {
  const [showPassword, setShowPassword] = React.useState(false);

  const Icon = showPassword ? EyeOff : Eye;

  const handleClick = () => setShowPassword((prev) => !prev);

  return (
    <div className="relative">
      <Input type={showPassword ? 'text' : 'password'} className={cn(className)} {...props} />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-0 top-1/2 -translate-y-1/2"
        onClick={handleClick}
      >
        <Icon className="size-4" />
      </Button>
    </div>
  );
};

export { Input, InputPassword, inputVariants };
export type { InputProps };
